import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeAnnouncements.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, Plus, Pencil } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeTable from '../components/MicrositeTable/MicrositeTable'
import MicrositePagination from '../components/MicrositePagination/MicrositePagination'
import ToggleSwitch from '../components/ToggleSwitch/ToggleSwitch'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'
import TextInput from '../components/TextInput/TextInput'
import MicrositeAnnouncementPreview from '../components/MicrositeAnnouncementPreview/MicrositeAnnouncementPreview'

const PAGE_LIMIT = 20

const EMPTY_FORM = {
    bannerText: '',
    linkText: '',
    linkUrl: '',
    bgColor: '#0d1117',
    textColor: '#ffffff',
    isDismissible: true,
    sortOrder: 0,
    isActive: true
}

const MicrositeAnnouncements = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [rows, setRows] = useState([])
    const [cursorStack, setCursorStack] = useState([0])
    const [nextCursor, setNextCursor] = useState(null)

    const [editorOpen, setEditorOpen] = useState(false)
    const [editingId, setEditingId] = useState(null)
    const [form, setForm] = useState(EMPTY_FORM)
    const [saving, setSaving] = useState(false)

    const currentCursor = cursorStack[cursorStack.length - 1]

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadAnnouncements = useCallback(async (cursor) => {
        setLoading(true)
        const res = await post('micrositeGetAnnouncements', { limit: PAGE_LIMIT, cursor: cursor || 0 })
        if (res?.status === 'success') {
            setRows(res.response?.data ?? [])
            setNextCursor(res.response?.nextCursor ?? null)
        } else {
            showToast('Could not load announcements. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadAnnouncements(currentCursor)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, currentCursor])

    const openNew = () => {
        setEditingId(null)
        setForm(EMPTY_FORM)
        setEditorOpen(true)
    }

    const openEdit = (row) => {
        setEditingId(row.ID)
        setForm({
            bannerText: row.bannerText || '',
            linkText: row.linkText || '',
            linkUrl: row.linkUrl || '',
            bgColor: row.bgColor || '#0d1117',
            textColor: row.textColor || '#ffffff',
            isDismissible: row.isDismissible ?? true,
            sortOrder: row.sortOrder ?? 0,
            isActive: row.isActive ?? true
        })
        setEditorOpen(true)
    }

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

    const handleSave = async () => {
        if (!form.bannerText.trim()) {
            showToast('Banner text is required', 'error')
            return
        }
        setSaving(true)
        const res = editingId
            ? await post('micrositeUpdateAnnouncement', { ID: editingId, ...form })
            : await post('micrositeCreateAnnouncement', { ...form })
        setSaving(false)

        if (res?.status === 'success') {
            showToast('Announcement saved successfully')
            setEditorOpen(false)
            loadAnnouncements(currentCursor)
        } else {
            showToast('Could not save announcement', 'error')
        }
    }

    const handleToggleActive = async (row, isActive) => {
        setRows((prev) => prev.map((r) => (r.ID === row.ID ? { ...r, isActive } : r)))
        const res = await post('micrositeUpdateAnnouncement', { ID: row.ID, ...row, isActive })
        if (res?.status !== 'success') {
            showToast('Could not update status', 'error')
            setRows((prev) => prev.map((r) => (r.ID === row.ID ? { ...r, isActive: !isActive } : r)))
        }
    }

    const handleDelete = async (ID) => {
        const res = await post('micrositeDeleteAnnouncement', { ID })
        if (res?.status === 'success') {
            setRows((prev) => prev.filter((r) => r.ID !== ID))
            showToast('Announcement deleted')
        } else {
            showToast('Could not delete announcement', 'error')
        }
    }

    const columns = [
        {
            key: 'bannerText',
            label: 'Banner',
            render: (row) => (
                <div className={styles.bannerCell}>
                    <span className={styles.swatch} style={{ background: row.bgColor }} />
                    <span className={styles.bannerText}>{row.bannerText}</span>
                </div>
            )
        },
        {
            key: 'isActive',
            label: 'Active',
            render: (row) => (
                <ToggleSwitch size="sm" checked={!!row.isActive} onChange={(v) => handleToggleActive(row, v)} />
            )
        },
        {
            key: 'actions',
            label: '',
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <button type="button" className={styles.editBtn} onClick={() => openEdit(row)}>
                        <Pencil size={13} />
                    </button>
                    <MicrositeDeleteConfirm mode="inline" onConfirm={() => handleDelete(row.ID)} />
                </div>
            )
        }
    ]

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Announcements"
                subtitle="Banner strips shown at the top of your site for offers or updates."
                primaryAction={{ label: 'New announcement', icon: <Plus size={14} />, onClick: openNew }}
            />

            {editorOpen && (
                <section className={styles.editorPanel}>
                    <div className={styles.editorLayout}>
                        <div className={styles.editorForm}>
                            <TextInput
                                label="Banner text"
                                value={form.bannerText}
                                onChange={(v) => update('bannerText', v)}
                                placeholder="e.g. Free blow-dry on bookings above ₹999!"
                                required
                            />
                            <div className={styles.row2}>
                                <TextInput
                                    label="Link text (optional)"
                                    value={form.linkText}
                                    onChange={(v) => update('linkText', v)}
                                    placeholder="Book now"
                                />
                                <TextInput
                                    label="Link URL"
                                    value={form.linkUrl}
                                    onChange={(v) => update('linkUrl', v)}
                                    placeholder="/book"
                                />
                            </div>
                            <div className={styles.row2}>
                                <div className={styles.colorField}>
                                    <label className={styles.colorLabel}>Background color</label>
                                    <input
                                        type="color"
                                        className={styles.colorInput}
                                        value={form.bgColor}
                                        onChange={(e) => update('bgColor', e.target.value)}
                                    />
                                </div>
                                <div className={styles.colorField}>
                                    <label className={styles.colorLabel}>Text color</label>
                                    <input
                                        type="color"
                                        className={styles.colorInput}
                                        value={form.textColor}
                                        onChange={(e) => update('textColor', e.target.value)}
                                    />
                                </div>
                            </div>
                            <ToggleSwitch
                                label="Dismissible"
                                description="Visitors can close this banner"
                                checked={form.isDismissible}
                                onChange={(v) => update('isDismissible', v)}
                            />
                            <div className={styles.editorActions}>
                                <button type="button" className={styles.cancelBtn} onClick={() => setEditorOpen(false)} disabled={saving}>
                                    Cancel
                                </button>
                                <button type="button" className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                                    {saving ? 'Saving…' : 'Save announcement'}
                                </button>
                            </div>
                        </div>
                        <div className={styles.editorPreview}>
                            <MicrositeAnnouncementPreview
                                bannerText={form.bannerText}
                                linkText={form.linkText}
                                bgColor={form.bgColor}
                                textColor={form.textColor}
                                isDismissible={form.isDismissible}
                            />
                        </div>
                    </div>
                </section>
            )}

            <MicrositeTable
                columns={columns}
                rows={rows}
                getRowKey={(row) => row.ID}
                loading={loading}
                emptyMessage="No announcements yet"
                emptyDescription="Create one to highlight an offer or update at the top of your site"
            />

            <MicrositePagination
                hasPrev={cursorStack.length > 1}
                hasNext={!!nextCursor}
                onPrev={() => setCursorStack((s) => s.slice(0, -1))}
                onNext={() => setCursorStack((s) => [...s, nextCursor])}
                loading={loading}
                currentCount={rows.length}
            />
        </div>
    )
}

export default MicrositeAnnouncements